import React, { useEffect, useState } from 'react';
import { Form, Input, Upload } from 'antd';
import { ProPageHeader as PageHeader } from '@ant-design/pro-layout';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  getTeamDetails,
  updateTeam,
  API_URL,
} from '../../../store/slices/adminSlice';
import CustomModal from '../../../components/common/CustomModal';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import type { UploadChangeParam, UploadFile } from 'antd/es/upload';

// TypeScript interfaces
interface TeamDetailsData {
  _id: string;
  teamname: string;
  teamLogo: string;
  sport?: {
    sportname: string;
  };
}

interface TeamFormValues {
  teamname: string;
}

interface RouteParams {
  teamId: string;
}

// Helper function to convert file to base64
const getBase64 = (file: Blob, callback: (url: string) => void): void => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(file);
};

const TeamDetail: React.FC = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm<TeamFormValues>();
  const { teamId } = useParams<RouteParams>();

  // Redux state
  const teamDetails = useAppSelector((state) => state.admin.teamDetails) as TeamDetailsData;

  // Local state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loading, setLoading] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [visible, setVisible] = useState<boolean>(false);
  const [changeImage, setChangeImage] = useState<boolean>(false);

  // Load data on mount
  useEffect(() => {
    if (teamId) {
      const obj = {
        teamId: teamId,
      };
      dispatch(getTeamDetails(obj));
    }
  }, [teamId, dispatch]);

  // Update image URL when team details change
  useEffect(() => {
    if (teamDetails) {
      setImageUrl(teamDetails.teamLogo);
      setLoading(false);
      setChangeImage(false);
    }
  }, [teamDetails]);

  const showModal = () => {
    setVisible(true);
    const team = teamDetails;
    form.setFieldsValue({
      teamname: team?.teamname || '',
    });
  };

  const handleChange = async (info: UploadChangeParam<UploadFile>) => {
    if (info.file.status === 'uploading') {
      return;
    }
    if (info.file.status === 'done' && info.file.originFileObj) {
      getBase64(info.file.originFileObj, (url) => {
        setImageUrl(url);
        setChangeImage(true);
      });

      const formData = new FormData();
      formData.append('file', info.file.originFileObj);
      formData.append('teamId', teamId!);

      // Update team logo
      const headers = {
        'Content-Type': 'multipart/form-data',
        Authorization: localStorage.getItem('access_token') || '',
      };
      await axios.post(API_URL + 'team/updateTeamLogo', formData, { headers });
    }
  };

  const upload = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('teamId', teamId!);

    const headers = {
      'Content-Type': 'multipart/form-data',
      Authorization: localStorage.getItem('access_token') || '',
    };
    await axios.post(API_URL + 'team/updateTeamLogo', formData, { headers });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setVisible(false);
      const obj = {
        teamname: values.teamname,
        teamId: teamId,
      };
      dispatch(updateTeam(obj));
    } catch {
      // Validation failed - form will show errors
    }
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const team = teamDetails || ({} as TeamDetailsData);
  const teamLogo = imageUrl || teamDetails?.teamLogo || '';

  return (
    <>
      <div className="overflowBox">
        <PageHeader prefixedClassName="ant-page-header" title="TEAM PROFILE" subTitle="" />
        <div className="box box-default mb-4">
          <div className="box-body">
            <div className="row profileDetails">
              <div className="col-sm-6 col-md-6">
                <div className="row">
                  <div className="col-sm-6 col-md-4">
                    <Upload
                      name="file"
                      listType="picture-card"
                      className="avatar-uploader"
                      showUploadList={false}
                      customRequest={({ file }) => upload(file as File)}
                      onChange={handleChange}
                    >
                      {changeImage ? (
                        <img
                          src={teamLogo ? teamLogo : 'http://placehold.it/380x500'}
                          alt="profile"
                          className="img-rounded img-responsive img-104"
                        />
                      ) : (
                        <img
                          src={teamLogo ? teamLogo : 'http://placehold.it/380x500'}
                          alt="profile"
                          className="img-rounded img-responsive img-104"
                        />
                      )}
                    </Upload>
                  </div>
                  <div className="col-sm-6 col-md-6">
                    <h4>
                      <i className="fa fa-users"></i> {team.teamname} &nbsp;
                    </h4>
                    {team && team.sport ? (
                      <h4>
                        <i className="fa fa-soccer-ball-o"></i> {team.sport.sportname} &nbsp;
                      </h4>
                    ) : null}
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-md-6" style={{ textAlign: 'right' }}>
                <i className="fa fa-pencil btn" style={{ color: '#666' }} onClick={showModal}></i>
              </div>
            </div>
          </div>
        </div>
      </div>
      <CustomModal
        handleCancel={handleCancel}
        handleOk={handleOk}
        visible={visible}
        title="Team Details"
        backBtnText="Back"
        submitBtnText="Submit"
      >
        <Form form={form} className="profile-form l_hgt0" layout="vertical">
          <Form.Item
            label="Teamname"
            name="teamname"
            rules={[
              { transform: (value: string) => value?.trim() },
              { required: true, message: 'Please enter team name' },
            ]}
          >
            <Input placeholder="Enter Team Name" />
          </Form.Item>
        </Form>
      </CustomModal>
    </>
  );
};

export default TeamDetail;
